<template>
  <div
    class="hidden md:flex md:flex-row z-10 px-4 lg:px-6 pt-2 lg:pt-4 pb-1 lg:pb-2"
  >
    <a href="https://pinseekergame.com">
      <img
        src="@/venue_locator/assets/images/pinseeker-logo-horizontal.svg"
        class="h-6"
      />
    </a>
    <div class="flex items-center">
      <p class="text-white pl-4">Where to Play</p>
    </div>
  </div>
  <div
    class="border-t-2 lg:border-t-0 border-b z-10 px-4 lg:px-6 pb-2 lg:pb-4 sticky top-0 left-0 right-0 action-container border-gray-800 bg-black"
  >
    <div
      class="transition-all lg:px-4 py-0 lg:bg-gray-800 flex flex-1 gap-x-3 self-stretch ring-green-300 lg:gap-x-4 rounded"
      :class="{ 'lg:ring-2': highlightInput }"
    >
      <form @submit.prevent="onSubmit" class="relative flex flex-1" action="#">
        <div
          class="pt-1.5 md:-ml-3.5 md:p-3 text-gray-400 hover:text-gray-500"
          title="Search"
        >
          <span v-if="!locationsStore.loading" class="sr-only">Search</span>
          <div v-if="locationsStore.loading" role="status">
            <svg
              aria-hidden="true"
              class="w-6 h-6 text-gray-200 animate-spin dark:text-gray-900 fill-green-300"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span class="sr-only">Loading...</span>
          </div>
          <svg
            v-if="!locationsStore.loading"
            class="h-6 w-6"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
              clip-rule="evenodd"
            ></path>
          </svg>
        </div>
        <label for="search-field" class="sr-only">Search zip or city</label>
        <input
          ref="search"
          v-model="searchTerm"
          :disabled="locationsStore.loading"
          id="search-field"
          class="pl-2 md:pl-0 py-2 lg:py-4 block h-full w-full border-0 py-0 pr-0 bg-black lg:bg-gray-800 text-gray-200 placeholder:text-gray-400 focus:ring-0 sm:text-sm"
          placeholder="Search zip or city..."
          type="search"
          name="search"
          @keyup.enter="search()"
        />
        <input type="submit" class="hidden" />
      </form>
      <div class="flex items-center gap-x-4 lg:gap-x-4"></div>
      <div
        v-if="!locationsStore.loading"
        class="flex items-center gap-x-4 lg:gap-x-6"
      >
        <button
          @click="useBrowserLocation"
          type="button"
          class="-m-2.5 p-2.5 text-gray-400 hover:text-gray-500"
          title="Use my location"
        >
          <span v-if="!locationsStore.loading" class="sr-only"
            >Use my location</span
          >
          <svg
            v-if="!locationsStore.loading"
            class="h-6 w-6"
            viewBox="0 0 22 22"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M3.619 9.06571C3.42403 9.06571 3.28077 8.88239 3.33684 8.69565C4.1138 6.10822 6.1654 4.07226 8.76222 3.31718C8.94845 3.26303 9.12997 3.40613 9.12997 3.60007V4.54978C9.12997 4.68228 9.04278 4.79841 8.91726 4.84084C7.02322 5.48109 5.52042 6.97048 4.86188 8.85601C4.81857 8.98 4.70316 9.06571 4.57182 9.06571L3.619 9.06571ZM3.65966 13.0657C3.46256 13.0657 3.31899 13.2528 3.37906 13.4405C4.18621 15.9631 6.21112 17.941 8.76222 18.6828C8.94845 18.737 9.12997 18.5939 9.12997 18.3999V17.4502C9.12997 17.3177 9.04277 17.2016 8.91726 17.1592C7.06537 16.5332 5.5875 15.0954 4.90709 13.2693C4.86206 13.1484 4.74817 13.0657 4.6192 13.0657H3.65966ZM17.3808 13.0657C17.2518 13.0657 17.1379 13.1484 17.0929 13.2693C16.4431 15.0132 15.0661 16.4029 13.3305 17.0697C13.2112 17.1155 13.13 17.2287 13.13 17.3565V18.3195C13.13 18.5176 13.3189 18.6613 13.507 18.5993C15.9309 17.8001 17.8424 15.8736 18.6209 13.4405C18.681 13.2528 18.5374 13.0657 18.3403 13.0657H17.3808ZM18.381 9.06572C18.5759 9.06572 18.7192 8.88239 18.6631 8.69566C17.9132 6.19834 15.9759 4.21475 13.507 3.40071C13.3189 3.33866 13.13 3.48237 13.13 3.68051V4.64352C13.13 4.77132 13.2112 4.88446 13.3305 4.9303C15.1074 5.613 16.5085 7.05349 17.1381 8.856C17.1814 8.98 17.2968 9.06572 17.4281 9.06572L18.381 9.06572Z"
              fill="currentColor"
            />
            <path
              d="M11.13 1L11.13 20.5"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="square"
            />
            <path
              d="M20.75 11.0657L1.25 11.0657"
              stroke="currentColor"
              stroke-width="1.6"
              stroke-linecap="square"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { useLocationTermStore } from "../stores/locationTermStore";
import { useLocationsStore } from "../stores/locationsStore";
import { useMapStore } from "../stores/mapStore";
import Bugsnag from "@bugsnag/js";

export default {
  setup() {
    const locationTermStore = useLocationTermStore();
    const locationsStore = useLocationsStore();
    const mapStore = useMapStore();
    let searchTerm = null;

    return { searchTerm, locationTermStore, locationsStore, mapStore };
  },

  async mounted() {
    let urlSearchParam = new URLSearchParams(document.location.search).get(
      "search",
    );
    let mapCenterConfig = null;

    this.locationsStore.loading = true;

    if (urlSearchParam !== null) {
      mapCenterConfig =
        await this.getMapCenterConfigFromSearchTerm(urlSearchParam);
    }

    if (mapCenterConfig == null) {
      mapCenterConfig = await this.getMapCenterConfigFromBrowserLocation();
    }

    if (mapCenterConfig == null) {
      mapCenterConfig = await this.getMapCenterConfigFromRequestIp();
    }

    if (mapCenterConfig == null) {
      Bugsnag.notify(
        new Error("Unable to obtain map center config. Using fallback."),
        function (event) {
          event.severity = "warning";
        },
      );

      // Fall back to the center of the US
      mapCenterConfig = {
        coords: { latitude: 39.1116, longitude: -97.2991 },
        zoom: { desktop: 5.3, mobile: 3 },
      };
    }

    this.mapStore.setCenter(mapCenterConfig.coords, mapCenterConfig.zoom);
  },

  data() {
    return {
      highlightInput: false,
    };
  },

  methods: {
    async search() {
      this.highlightInput = false;

      // searched with an empty input field
      if (this.searchTerm == undefined) {
        this.$refs.search.focus();
        this.highlightInput = true;
        return;
      }

      this.locationsStore.loading = true;

      let mapCenterConfig = await this.getMapCenterConfigFromSearchTerm(
        this.searchTerm,
      );

      if (mapCenterConfig !== null) {
        this.mapStore.setCenter(mapCenterConfig.coords, mapCenterConfig.zoom);
      } else {
        this.locationsStore.loading = false;
      }

      this.$gtag.event("Venue Search", { term: this.searchTerm });
    },

    async useBrowserLocation() {
      this.locationsStore.loading = true;

      let mapCenterConfig = await this.getMapCenterConfigFromBrowserLocation();

      if (mapCenterConfig !== null) {
        this.mapStore.setCenter(mapCenterConfig.coords, mapCenterConfig.zoom);
      }
    },

    async getMapCenterConfigFromSearchTerm(searchTerm) {
      let mapCenter = this.mapStore.center;
      let url = `/coords_from_search_term.json?search_term=${searchTerm}`;

      if (mapCenter) {
        url += `&latitude=${mapCenter.latitude}&longitude=${mapCenter.longitude}`;
      }

      let coords = await this.fetchCoords(url);

      if (coords !== null) {
        return {
          coords: coords,
          zoom: { desktop: 11, mobile: 9 },
        };
      } else {
        return null;
      }
    },

    getMapCenterConfigFromBrowserLocation() {
      return new Promise((resolve, _reject) => {
        if (navigator.geolocation) {
          this.$gtag.event("Browser Geolocation Available");

          navigator.geolocation.getCurrentPosition(
            (position) => {
              resolve(position.coords);
            },
            async (error) => {
              this.$gtag.event("Browser Geolocation Error", {
                error_code: error.code,
              });
              console.error(error);

              resolve(null);
            },
          );
        } else {
          this.$gtag.event("Browser Geolocation Not Supported");

          resolve(null);
        }
      }).then((coords) => {
        if (coords !== null) {
          return {
            coords: coords,
            zoom: { desktop: 11, mobile: 9 },
          };
        } else {
          return null;
        }
      });
    },

    async getMapCenterConfigFromRequestIp() {
      let coords = await this.fetchCoords(`/coords_from_request_ip.json`);

      if (coords !== null) {
        return {
          coords: coords,
          zoom: { desktop: 9, mobile: 7 },
        };
      } else {
        return null;
      }
    },

    async fetchCoords(url) {
      try {
        let response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        let data = await response.json();

        return data.coordinates;
      } catch (error) {
        Bugsnag.notify(error);
        console.error("There was an error!", error);

        return null;
      }
    },
  },
};
</script>
